import { useState, useEffect, ChangeEvent } from 'react';
import StepProgress from '../../../juristec-ui/core/StepProgress';
import {
  SelectFile, BindVariables, Filters, Submit,
} from '../CopyDashTemplate';
import DashStyles from '../../DashStyles';
import { verifyInput } from '../../../juristec-ui/utils/validators/inputTextValidators';
import { defaultDashStyle } from '../../../options';
import {
  Template,
  BorderStyle,
  ColumnType,
  File,
  StylesData,
  TourContext,
  CardStyles,
  NewDashName,
  FilterColumn,
} from '../CopyDashTemplate/types';

interface ApplyTemplateProps {
  hide: () => void;
  template?: Template;
  files?: File[];
  loadFile: (fileId: string) => Promise<ColumnType>;
  onSelectFilter: (fileId: string, columnValue: string, dateFormat: string) => Promise<any[]>;
  submitData: (data: {
    file: string;
    name: string;
    bindings: Record<string, unknown>;
    filters: unknown[];
    cardStyles: {
      backgroundColor: string;
      shading: boolean;
      borderStyle: BorderStyle;
      spaceBetween: number;
      useGlobalTheme: boolean;
      titles: unknown;
      charts: unknown[];
      insightBg: string;
      insightTxt: string;
      valueBg: string;
      valueTxt: string;
      tableRows: unknown;
      tableHeader: unknown;
    };
  }) => void;
  stylesData?: StylesData;
  tourContext?: TourContext;
}

const applyTemplateDefaultPropTemplate: Template = {
  columnsRequired: {},
  columnsType: { abc: [], float: [], date: [] },
};
const applyTemplateDefaultPropFiles: File[] = [];
const applyTemplateDefaultPropTourContext: TourContext = {
  tourOpen: false,
};
const applyTemplateDefaultPropStylesData: StylesData = {};
const legacyTypes = {
  float: 'float64',
  date: 'datetime64[ns]',
  abc: 'category',
};

/**
 * Component with steps to apply a template to a file
 */
const ApplyTemplate = ({
  hide,
  template = applyTemplateDefaultPropTemplate,
  files = applyTemplateDefaultPropFiles,
  loadFile,
  onSelectFilter,
  submitData,
  stylesData = applyTemplateDefaultPropStylesData,
  tourContext = applyTemplateDefaultPropTourContext,
}: ApplyTemplateProps) => {
  const [columnVarBinds, setColumnVarBinds] = useState<Record<string, string>>({});
  const [fileColumns, setFileColumns] = useState<ColumnType>({ abc: [], float: [], date: [] });
  const [filterColumns, setFilterColumns] = useState<FilterColumn[]>([]);
  const [cardStyles, setCardStyles] = useState<CardStyles>(defaultDashStyle);
  const [newDashName, setNewDashName] = useState<NewDashName>({
    value: '',
    error: true,
    errorMsg: '',
  });
  const [selectedFile, setSelectedFile] = useState<File>({ label: '', value: '', filename: undefined });

  interface Option {
    id: string;
    label: string;
    value: string;
    type: string;
  }

  const [options, setOptions] = useState<Option[]>([]);

  useEffect(() => {
    const newOptions = Object.keys(legacyTypes).reduce<Option[]>((acc, colType) => {
      const columnType = colType as keyof ColumnType;
      return [...acc, ...fileColumns[columnType].map((col) => ({
        id: col,
        label: col,
        value: col,
        type: legacyTypes[columnType],
      }))];
    }, []);
    setOptions(newOptions);
  }, [fileColumns]);

  const getColumnData = (column: string, selector: string) => onSelectFilter(
    selectedFile.file_id!, column, selector,
  );

  useEffect(() => {
    if (selectedFile?.file_id) {
      (async () => {
        setFileColumns(await loadFile(selectedFile.file_id!));
      })();
    }
  }, [selectedFile]);

  useEffect(() => {
    if (stylesData) {
      setCardStyles({
        ...defaultDashStyle,
        ...stylesData,
        borderLine: stylesData.borderStyle?.line ?? defaultDashStyle.borderLine,
        borderColor: stylesData.borderStyle?.color ?? defaultDashStyle.borderColor,
        borderSize: stylesData.borderStyle?.size ?? defaultDashStyle.borderSize,
        borderRounded: stylesData.borderStyle?.rounded ?? defaultDashStyle.borderRounded,
        spaceBetween: stylesData.spaceBetween !== undefined ? (
          +stylesData.spaceBetween / 10
        ) : defaultDashStyle.spaceBetween,
      });
    }
  }, [stylesData]);

  const checkVarBinds = () => (
    Object.keys(template!.columnsRequired).length
      === Object.keys(columnVarBinds).length
  );

  const checkColumnsVar = () => (
    (fileColumns.abc?.length || 0) >= (template!.columnsType.abc?.length || 0)
    && (fileColumns.float?.length || 0) >= (template!.columnsType.float?.length || 0)
    && (fileColumns.date?.length || 0) >= (template!.columnsType.date?.length || 0)
  );

  const newDashNameHandle = (val: string | ChangeEvent<HTMLInputElement>): void => {
    const msg = verifyInput(val, true, 2, 40);
    setNewDashName({
      value: val as string,
      error: msg.length !== 0,
      errorMsg: msg,
    });
  };

  const checkNewDashName = () => !newDashName.error;

  const handleSubmit = () => {
    submitData({
      file: selectedFile.value,
      name: newDashName.value,
      bindings: columnVarBinds,
      filters: filterColumns,
      cardStyles: {
        backgroundColor: cardStyles.backgroundColor,
        shading: cardStyles.shading,
        borderStyle: {
          color: cardStyles.borderColor,
          line: cardStyles.borderLine,
          rounded: (+cardStyles.borderRounded),
          size: (+cardStyles.borderSize),
        },
        spaceBetween: (+cardStyles.spaceBetween) * 10,
        useGlobalTheme: cardStyles.useGlobalTheme,
        titles: cardStyles.titles,
        charts: cardStyles.charts.filter((c) => Boolean(c)),
        insightBg: cardStyles.insightBg,
        insightTxt: cardStyles.insightTxt,
        valueBg: cardStyles.valueBg,
        valueTxt: cardStyles.valueTxt,
        tableRows: cardStyles.tableRows,
        tableHeader: cardStyles.tableHeader,
      },
    });
  };

  const firstStep = {
    label: 'Arquivo',
    validation: checkColumnsVar,
    content: (
      <SelectFile
        files={files}
        selected={template}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        checkColumnsVar={checkColumnsVar}
        fileColumns={fileColumns}
      />
    ),
  };

  const secondStep = {
    label: 'Variáveis',
    validation: checkVarBinds,
    content: (
      <BindVariables
        selected={template}
        columnVarBinds={columnVarBinds}
        setColumnVarBinds={setColumnVarBinds}
        fileColumns={fileColumns}
      />
    ),
  };

  const thirdStep = {
    label: 'Filtro',
    content: (
      <Filters
        fileColumns={options}
        getColumnData={getColumnData}
        filterColumns={filterColumns}
        setFilterColumns={setFilterColumns}
      />
    ),
  };

  const fourthStep = {
    label: 'Estilo',
    content: (
      <DashStyles
        tempStylesDataState={[cardStyles, setCardStyles]}
      />
    ),
  };

  const fifthStep = {
    label: 'Criação',
    validation: checkNewDashName,
    content: (
      <Submit
        newDashName={newDashName}
        newDashNameHandle={newDashNameHandle}
      />
    ),
  };

  return (
    <StepProgress
      steps={[firstStep, secondStep, thirdStep, fourthStep, fifthStep]}
      hide={hide}
      complete={handleSubmit}
      markersWidth="large"
      disableUntilValid
      tourContext={tourContext}
    />
  );
};

export default ApplyTemplate;
